// Tela /attendant-goal-trial (feature 161, spec seção 8): convite pendente, degustação ativa e resultado.
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Title, useNotify, usePermissions, useRefresh } from 'react-admin';
import { Alert, Box, Button, Chip, LinearProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { __t } from '../../i18n/t';
import AiInsightPanel from '../../components/AiInsightPanel';
import {
  fixedHeadline, goalLevelLabel, goalTypeLabel, goalValueShort, goalValueText, insightKindFor, offerErrorText,
} from '../../components/AttendantGoalOfferCard/offerCopy';
import { useOfferGate } from '../../components/AttendantGoalOfferCard/useOfferGate';
import { useOfferInsight } from '../../components/AttendantGoalOfferCard/useOfferInsight';
import { PERMISSIONS } from '../../constants';
import {
  GoalLevel, GoalTrigger, InsightVerdict, OfferApiError, OfferEndedReason, OfferErrorCode, OfferStationView, OfferStatus, OfferView,
} from '../../models/attendantGoalOffer';
import { activateOffer, getOffer, markOfferSeen, requestContinue } from '../../services/attendantGoalOfferApi';
import { monitorPath } from '../attendantGoalBoardMonitor/monitorLink';
import { COLORS, cardSx } from '../dashboards/shared';
import ActiveBoards from './ActiveBoards';
import { firstStationBoards } from './boards';
import { extraGoalValue, initialLevels, levelsPayload, mergeLevels, selectableStations } from './levels';
import PlaceLink from './PlaceLink';

const TRIAL_DAYS = 60;

const stationSummary = (s: OfferStationView): string[] => {
  const lines: string[] = [];
  if (s.triggers.includes(GoalTrigger.lowTail)) {
    lines.push(s.tailIndexPct == null
      ? __t('Frentistas a acompanhar: %{tail} de %{total}', { tail: s.tailSize, total: s.eligibleAttendants })
      : __t('Frentistas a acompanhar: %{tail} de %{total}, com rendimento cerca de %{pct}% abaixo da equipe nas mesmas horas',
        { tail: s.tailSize, total: s.eligibleAttendants, pct: 100 - s.tailIndexPct }));
  }
  if (s.triggers.includes(GoalTrigger.loyaltyGap)) {
    lines.push(__t('Frentistas a acompanhar na fidelização: %{n}', { n: s.loyaltyLowCount }));
    if (s.stationAppPct != null) lines.push(__t('Fidelização do posto: %{pct}%', { pct: s.stationAppPct }));
  }
  return lines;
};

const verdictText = (v: InsightVerdict | undefined): string => {
  if (v === InsightVerdict.positive) return __t('Indicativo: a equipe rendeu mais durante a degustação');
  if (v === InsightVerdict.negative) return __t('Indicativo: a equipe rendeu menos no período');
  if (v === InsightVerdict.neutral) return __t('Indicativo: sem mudança relevante');
  return __t('Dados insuficientes para um veredito');
};

/**
 * "Também vamos acompanhar": as demais metas do posto no valor recomendado. Backend antigo não manda `extraGoals`: nada aparece.
 * (Componente à parte: o plugin de i18n embrulha em __t() todo literal dentro de `cond && <X sx=.../>`, inclusive valor de CSS.)
 */
const ExtraGoals = ({ station }: { station: OfferStationView }) => {
  const extras = (station.extraGoals ?? []).map((g) => ({ goal: g, value: extraGoalValue(g) })).filter((e) => e.value !== null);
  if (extras.length === 0) return null;
  return (
    <Box sx={{ mt: 0.5, pt: 1.25, borderTop: '1px solid rgba(35,35,35,0.08)', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}>{__t('Também vamos acompanhar')}</Typography>
      {/* Duas colunas: o nome do tipo e o valor ficam alinhados entre as linhas. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', columnGap: 1.25, rowGap: 0.75, alignItems: 'center' }}>
        {extras.map(({ goal, value }) => (
          <Fragment key={goal.targetType}>
            <Chip size="small" variant="outlined" label={goalTypeLabel(goal.targetType)} sx={{ justifySelf: 'start' }} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>
              {goalValueShort(goal.targetType, value as number)}
            </Typography>
          </Fragment>
        ))}
      </Box>
      <Typography sx={{ fontSize: 12, color: COLORS.muted }}>{__t('No valor recomendado pelo histórico do posto.')}</Typography>
    </Box>
  );
};

// ─── bloco de um posto no convite pendente ───────────────────────────────────────
const StationBlock = ({ station, level, onChange }: { station: OfferStationView; level: GoalLevel; onChange: (l: GoalLevel) => void }) => {
  const selected = station.options.find((o) => o.level === level) ?? station.options[0];
  return (
    <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <PlaceLink placeId={station.placeId} label={station.placeName} />
        <Chip size="small" label={goalTypeLabel(station.targetType)} />
      </Box>
      {stationSummary(station).map((line, i) => (
        <Typography key={i} sx={{ fontSize: 12.5, color: '#334155' }}>{line}</Typography>
      ))}
      {station.singleLevel || station.options.length === 1 ? (
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>
          {__t('Meta sugerida: %{valor}', { valor: goalValueText(station.targetType, selected.value) })}
        </Typography>
      ) : (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={selected.level}
          onChange={(_, v: GoalLevel | null) => { if (v) onChange(v); }}
          sx={{ flexWrap: 'wrap', alignSelf: 'flex-start' }}
        >
          {station.options.map((o) => (
            <ToggleButton key={o.level} value={o.level} sx={{ textTransform: 'none', px: 1.75, flexDirection: 'column', alignItems: 'flex-start' }}>
              <span>{goalLevelLabel(o.level)}</span>
              <b>{goalValueText(station.targetType, o.value)}</b>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
        {__t('%{reaching} de %{total} frentistas já bateriam esta meta', { reaching: selected.attendantsReaching, total: station.eligibleAttendants })}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
        {__t('Dias batidos no histórico: %{pct}%', { pct: selected.daysHitRatePct })}
      </Typography>
      <ExtraGoals station={station} />
    </Box>
  );
};

// ─── a tela ──────────────────────────────────────────────────────────────────────
const AttendantGoalTrial = () => {
  const { allowed, loading: gateLoading } = useOfferGate();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const canMonitor = !!permissions?.includes(PERMISSIONS.ATTENDANT_TARGET_MONITOR);
  const [offer, setOffer] = useState<OfferView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [levels, setLevels] = useState<Record<string, GoalLevel>>({});
  const seenFor = useRef<string | null>(null);

  // Troca o convite e acerta os níveis: sem `previous` (primeira carga) vale o já escolhido/recomendado;
  // com `previous` (stale_suggestion, erro de ativação) mescla a escolha do dono (F2).
  const applyOffer = useCallback((next: OfferView | null, previous?: Record<string, GoalLevel>) => {
    setOffer(next);
    if (next?.status === OfferStatus.pending) {
      setLevels(previous ? mergeLevels(next.stations, previous) : initialLevels(selectableStations(next.stations)));
    }
  }, []);

  const reload = useCallback(async (previous?: Record<string, GoalLevel>) => {
    try { applyOffer(await getOffer(), previous); setLoadFailed(false); }
    catch { setLoadFailed(true); notify(__t('Não foi possível carregar o convite. Tente novamente.'), { type: 'error' }); } // erro não é "sem convite": mantém o atual
    finally { setLoaded(true); }
  }, [applyOffer, notify]);
  useEffect(() => { if (allowed) reload(); }, [allowed, reload]);

  // Ao abrir: `seen` uma vez por convite (o backend só usa para o Discord; falha não importa).
  useEffect(() => {
    if (offer && seenFor.current !== offer.id) {
      seenFor.current = offer.id;
      markOfferSeen(offer.id).catch(() => undefined);
    }
  }, [offer]);

  const { content, decisions, loading: insightLoading } = useOfferInsight(
    offer?.id, offer ? insightKindFor(offer.status) : undefined, offer?.suggestionVersion,
  );

  if (gateLoading) return null;
  if (!allowed) return <Navigate to="/" replace />;
  if (!loaded) return <LinearProgress />;
  if (!offer && loadFailed) {
    return <Box sx={{ p: 2 }}><Alert severity="error" action={<Button color="inherit" size="small" onClick={() => reload()}>{__t('Tentar novamente')}</Button>}>{__t('Não foi possível carregar o convite. Tente novamente.')}</Alert></Box>;
  }
  if (!offer) return <Navigate to="/" replace />;

  const activate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const active = await activateOffer(offer.id, offer.suggestionVersion, levelsPayload(selectableStations(offer.stations), levels));
      setOffer(active);
      refresh();
      notify(__t('Degustação ativada. Os quadros de metas já foram criados.'), { type: 'success' });
      // Direto para o ranking ao vivo: primeiro posto com todos os quadros dele já selecionados (sem cliques no monitor).
      const first = firstStationBoards(active.stations);
      if (first && canMonitor) navigate(monitorPath(first.placeId, first.boardIds));
    } catch (err) {
      const e = err as OfferApiError;
      notify(offerErrorText(e.code), { type: e.code === OfferErrorCode.staleSuggestion ? 'warning' : 'error' });
      if (e.code === OfferErrorCode.staleSuggestion && e.offer) applyOffer(e.offer, levels);
      else await reload(levels);
    } finally {
      setBusy(false);
    }
  };

  const askContinue = async () => {
    if (busy) return;
    setBusy(true);
    try {
      setOffer(await requestContinue(offer.id));
    } catch (err) {
      notify(offerErrorText((err as OfferApiError).code), { type: 'error' });
      await reload(levels);
    } finally {
      setBusy(false);
    }
  };

  // Na tela da degustação o painel se apresenta como análise da equipe (a IA fica por baixo dos panos).
  const analysisTitle = __t('Análise da sua equipe');
  const analysisLoading = __t('Analisando os dados da sua equipe…');

  const isPending = offer.status === OfferStatus.pending;
  const isActive = offer.status === OfferStatus.active;
  const converted = offer.status === OfferStatus.ended && offer.endedReason === OfferEndedReason.converted;

  const continueBlock = offer.continueRequestedAt
    ? <Chip color="success" variant="outlined" label="Recebemos seu pedido" sx={{ alignSelf: 'flex-start' }} />
    : offer.canContinue && (
      <Button variant="contained" disabled={busy} onClick={askContinue} sx={{ alignSelf: 'flex-start' }}>
        Quero continuar com desconto
      </Button>
    );

  return (
    <Box sx={{ maxWidth: 920, display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
      <Title title={__t('Gestão da equipe de pista')} />
      <Typography sx={{ fontSize: 20, fontWeight: 800, color: COLORS.ink }}>{__t('Gestão da equipe de pista')}</Typography>

      {isPending && (
        <>
          <Typography sx={{ fontSize: 13.5, color: '#334155' }}>
            Cada posto já tem metas calculadas com o histórico da pista. Escolha o nível e ative 60 dias grátis.
          </Typography>
          <AiInsightPanel content={content} loading={insightLoading} title={analysisTitle} loadingText={analysisLoading} />
          {!content && !insightLoading && (
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>{fixedHeadline(offer.status)}</Typography>
          )}
          {selectableStations(offer.stations).map((s) => (
            <StationBlock
              key={s.placeId}
              station={s}
              level={levels[s.placeId] ?? s.recommended}
              onChange={(l) => setLevels((prev) => ({ ...prev, [s.placeId]: l }))}
            />
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" disabled={busy} onClick={activate}>Ativar 60 dias grátis</Button>
            <Typography sx={{ fontSize: 12, color: COLORS.muted }}>Para continuar depois, tem desconto especial</Typography>
          </Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#3d6b46' }}>
            Sem custo e sem cobrança automática. No fim do teste, desliga sozinho.
          </Typography>
        </>
      )}

      {isActive && (
        <>
          <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: COLORS.ink }}>
              {offer.daysLeft === 1 ? __t('Degustação: falta 1 dia') : __t('Degustação: faltam %{n} dias', { n: offer.daysLeft ?? 0 })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, ((offer.dayNumber ?? 0) / TRIAL_DAYS) * 100)}
              sx={{ height: 8, borderRadius: 4, maxWidth: 420 }}
            />
            <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
              {__t('Dia %{dia} de %{total}', { dia: Math.min(offer.dayNumber ?? 1, TRIAL_DAYS), total: TRIAL_DAYS })}
            </Typography>
          </Box>
          <AiInsightPanel content={content} loading={insightLoading} title={analysisTitle} loadingText={analysisLoading} />
          {!content && !insightLoading && (
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>{fixedHeadline(offer.status)}</Typography>
          )}
          <ActiveBoards stations={offer.stations} canMonitor={canMonitor} />
          {continueBlock}
        </>
      )}

      {offer.status === OfferStatus.ended && (
        <>
          {converted ? (
            <Alert severity="success" sx={{ alignSelf: 'flex-start' }}>
              A gestão da equipe de pista já está ativa de forma definitiva na sua rede.
            </Alert>
          ) : (
            <>
              <Typography sx={{ fontSize: 13.5, color: '#334155' }}>
                Sua degustação terminou. Veja o antes e o depois da produtividade da equipe.
              </Typography>
              <AiInsightPanel
                content={content}
                loading={insightLoading}
                title={analysisTitle}
                loadingText={analysisLoading}
                chip={content && <Chip size="small" variant="outlined" label={verdictText(decisions?.verdict)} />}
              />
              {content && (
                <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                  É uma leitura indicativa: outros fatores do período também influenciam o resultado.
                </Typography>
              )}
              {!content && !insightLoading && (
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>{fixedHeadline(offer.status)}</Typography>
              )}
              {continueBlock}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default AttendantGoalTrial;
